import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminSidebar from '../components/AdminSidebar';
import { getAllOrders, updateOrderStatus } from '../utils/api';
import { Filter, CheckCircle, XCircle, Package } from 'lucide-react';
import { toast } from 'sonner';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter(order => order.status === statusFilter));
    }
  }, [statusFilter, orders]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await getAllOrders();
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success(`Order ${newStatus}`);
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-accent/20 text-accent';
      case 'accepted': return 'bg-primary/20 text-primary';
      case 'completed': return 'bg-primary/20 text-primary';
      case 'rejected': return 'bg-destructive/20 text-destructive';
      default: return 'bg-muted/20 text-muted-foreground';
    }
  };

  return (
    <div className="flex min-h-screen bg-[#0F1219]">
      <div className="scanline-overlay" />
      <AdminSidebar />

      <div className="flex-1 ml-64 p-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-heading font-bold text-white mb-2 tracking-wider" data-testid="orders-title">ORDER MANAGEMENT</h1>
          <p className="text-gray-400">Process and track all orders</p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 flex items-center gap-4"
        >
          <Filter className="text-primary" />
          <div className="flex gap-2">
            {['all', 'pending', 'accepted', 'completed', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 font-heading tracking-wider uppercase text-sm transition-colors ${
                  statusFilter === status
                    ? 'bg-primary text-black'
                    : 'border border-border text-gray-400 hover:border-primary/50 hover:text-primary'
                }`}
                data-testid={`filter-${status}`}
              >
                {status}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Orders Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border/50 p-8"
        >
          {loading ? (
            <div className="text-center py-12 text-primary font-mono text-xl">LOADING...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="orders-table">
                <thead>
                  <tr className="border-b border-primary/20">
                    <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Order ID</th>
                    <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Contact</th>
                    <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Pop (K)</th>
                    <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Total</th>
                    <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Status</th>
                    <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-12 text-gray-400 font-mono">
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order, index) => (
                      <tr key={order.id} className="border-b border-border/30 hover:bg-primary/5 transition-colors" data-testid={`order-row-${index}`}>
                        <td className="py-4 px-4 font-mono text-primary font-bold">{order.order_id}</td>
                        <td className="py-4 px-4">
                          <div className="text-white font-mono text-sm">{order.contact_value}</div>
                          <div className="text-gray-400 text-xs font-mono uppercase">{order.contact_method}</div>
                        </td>
                        <td className="py-4 px-4 font-mono text-white">{order.pop_needed_k}K</td>
                        <td className="py-4 px-4 font-mono text-primary font-bold">₹{order.total_price.toFixed(2)}</td>
                        <td className="py-4 px-4">
                          <span className={`px-3 py-1 text-xs font-mono uppercase tracking-wider ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            {order.status === 'pending' && (
                              <>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleStatusUpdate(order.order_id, 'accepted')}
                                  className="p-2 bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
                                  title="Accept"
                                  data-testid={`accept-button-${index}`}
                                >
                                  <CheckCircle size={18} />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleStatusUpdate(order.order_id, 'rejected')}
                                  className="p-2 bg-destructive/20 text-destructive hover:bg-destructive/30 transition-colors"
                                  title="Reject"
                                  data-testid={`reject-button-${index}`}
                                >
                                  <XCircle size={18} />
                                </motion.button>
                              </>
                            )}
                            {order.status === 'accepted' && (
                              <>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleStatusUpdate(order.order_id, 'completed')}
                                  className="p-2 bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
                                  title="Complete"
                                  data-testid={`complete-button-${index}`}
                                >
                                  <Package size={18} />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleStatusUpdate(order.order_id, 'rejected')}
                                  className="p-2 bg-destructive/20 text-destructive hover:bg-destructive/30 transition-colors"
                                  title="Reject"
                                  data-testid={`reject-after-accept-button-${index}`}
                                >
                                  <XCircle size={18} />
                                </motion.button>
                              </>
                            )}
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setSelectedOrder(order)}
                              className="p-2 border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                              title="View Details"
                              data-testid={`view-details-button-${index}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                            </motion.button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6"
          onClick={() => setSelectedOrder(null)}
          onKeyDown={(e) => e.key === 'Escape' && setSelectedOrder(null)}
          role="button"
          tabIndex={0}
          aria-label="Close modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border border-primary/50 p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            data-testid="order-details-modal"
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-heading font-bold text-primary tracking-wider">ORDER DETAILS</h3>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-white"
                data-testid="close-modal-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <DetailField label="Order ID" value={selectedOrder.order_id} />
              <DetailField label="Status" value={selectedOrder.status.toUpperCase()} />
              <DetailField label="Contact Method" value={selectedOrder.contact_method.toUpperCase()} />
              <DetailField label="Contact Value" value={selectedOrder.contact_value} />
              <DetailField label="UID" value={selectedOrder.uid} />
              <DetailField label="In-Game Name" value={selectedOrder.in_game_name} />
              <DetailField label="Pop Needed" value={`${selectedOrder.pop_needed_k}K`} />
              <DetailField label="Rate per K" value={`₹${selectedOrder.rate_per_k}`} />
              <DetailField label="Total Price" value={`₹${selectedOrder.total_price.toFixed(2)}`} mono bold />
              <DetailField label="Start Time" value={selectedOrder.start_time} />
              {selectedOrder.upi_ref && <DetailField label="UPI Reference" value={selectedOrder.upi_ref} />}
              <DetailField label="Created At" value={new Date(selectedOrder.created_at).toLocaleString()} />
              <DetailField label="Updated At" value={new Date(selectedOrder.updated_at).toLocaleString()} />
            </div>

            {/* Payment Screenshot Display */}
            {selectedOrder.payment_screenshot && (
              <div className="mb-6 p-4 bg-black/40 border border-primary/20">
                <h4 className="text-primary font-heading tracking-wider uppercase mb-3 text-sm">Payment Screenshot</h4>
                <div className="relative max-w-md">
                  <img 
                    src={selectedOrder.payment_screenshot} 
                    alt="Payment Screenshot" 
                    className="w-full h-auto border border-primary/30"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{display: 'none'}} className="text-gray-400 text-sm font-mono">
                    Screenshot unavailable or invalid format
                  </div>
                </div>
              </div>
            )}

            {selectedOrder.status === 'pending' && (
              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => handleStatusUpdate(selectedOrder.order_id, 'accepted')}
                  className="flex-1 px-6 py-3 bg-primary text-black font-heading font-bold tracking-wider uppercase flex items-center justify-center gap-2"
                  data-testid="modal-accept-button"
                >
                  <CheckCircle size={20} />
                  Accept
                </button>
                <button
                  onClick={() => handleStatusUpdate(selectedOrder.order_id, 'rejected')}
                  className="flex-1 px-6 py-3 bg-destructive text-white font-heading font-bold tracking-wider uppercase flex items-center justify-center gap-2"
                  data-testid="modal-reject-button"
                >
                  <XCircle size={20} />
                  Reject
                </button>
              </div>
            )}

            {selectedOrder.status === 'accepted' && (
              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => handleStatusUpdate(selectedOrder.order_id, 'completed')}
                  className="flex-1 px-6 py-3 bg-primary text-black font-heading font-bold tracking-wider uppercase flex items-center justify-center gap-2"
                  data-testid="modal-complete-button"
                >
                  <Package size={20} />
                  Complete
                </button>
                <button
                  onClick={() => handleStatusUpdate(selectedOrder.order_id, 'rejected')}
                  className="flex-1 px-6 py-3 bg-destructive text-white font-heading font-bold tracking-wider uppercase flex items-center justify-center gap-2"
                  data-testid="modal-reject-after-accept-button"
                >
                  <XCircle size={20} />
                  Reject
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

const DetailField = ({ label, value, mono = true, bold = false }) => (
  <div className="p-4 bg-black/40 border border-primary/20">
    <div className="text-gray-400 text-xs font-heading tracking-wider uppercase mb-1">{label}</div>
    <div className={`text-white ${mono ? 'font-mono' : ''} ${bold ? 'font-bold text-primary' : ''} text-sm break-all`}>
      {value}
    </div>
  </div>
);

export default AdminOrders;